import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ShareBar from '../components/ShareBar';

const API = process.env.REACT_APP_API_URL || '';

const QUESTIONS = [
  {
    question: 'You get an unexpected $500. What do you do?',
    options: [
      { label: 'A', text: 'Invest it immediately' },
      { label: 'B', text: 'Pay down debt' },
      { label: 'C', text: 'Save it for later' },
      { label: 'D', text: 'Treat yourself (you deserve it)' },
    ],
  },
  {
    question: 'Payday arrives. First thing you do?',
    options: [
      { label: 'A', text: 'Budget every dollar' },
      { label: 'B', text: 'Pay bills, spend the rest' },
      { label: 'C', text: 'Check my investments' },
      { label: 'D', text: 'Honestly, forget to check my account' },
    ],
  },
  {
    question: 'Your biggest money fear?',
    options: [
      { label: 'A', text: 'Not having enough in retirement' },
      { label: 'B', text: 'Drowning in debt' },
      { label: 'C', text: 'Missing a big market opportunity' },
      { label: 'D', text: 'Having to think about money at all' },
    ],
  },
  {
    question: 'How do you feel about budgeting?',
    options: [
      { label: 'A', text: 'I love a good spreadsheet' },
      { label: 'B', text: 'I try, but it never sticks' },
      { label: 'C', text: 'I just check my balance and wing it' },
      { label: 'D', text: 'Budget? What budget?' },
    ],
  },
  {
    question: 'You hear about a hot stock tip from a friend. You...',
    options: [
      { label: 'A', text: 'Research it for a week before touching it' },
      { label: 'B', text: "Ignore it — I don't gamble" },
      { label: 'C', text: 'Put a small amount in, just in case' },
      { label: 'D', text: "Already bought it before they finished the sentence" },
    ],
  },
  {
    question: 'Your long-term money goal?',
    options: [
      { label: 'A', text: 'Financial independence / retire early' },
      { label: 'B', text: 'Get completely debt-free' },
      { label: 'C', text: 'Build generational wealth' },
      { label: 'D', text: 'Honestly just be comfortable and not stressed' },
    ],
  },
];

const MONEY_TYPES = {
  A: {
    key: 'A',
    name: 'The Optimizer',
    emoji: '📈',
    color: '#38bdf8',
    colorBg: 'rgba(56,189,248,0.15)',
    tagline: "Analytical, strategic, driven. You engineer your financial life.",
    description:
      "You're analytical, strategic, and driven. You see money as a tool to engineer the life you want. Your superpower: compound interest is your best friend. Your blind spot: sometimes you optimize so hard you forget to live.",
    tips: [
      { text: 'Run your retirement numbers — see exactly how much you need.', link: '/calculators', cta: 'Open Calculators →' },
      { text: 'Track every dollar to find hidden optimization opportunities.', link: '/tracker', cta: 'Open Tracker →' },
      { text: 'Read the KnowFi Brief daily — stay ahead of the market.', link: '/brief', cta: 'Read the Brief →' },
    ],
  },
  B: {
    key: 'B',
    name: 'The Eliminator',
    emoji: '⚔️',
    color: '#f87171',
    colorBg: 'rgba(248,113,113,0.15)',
    tagline: "Debt is your nemesis. You're laser-focused on destroying it.",
    description:
      "You hate owing anyone anything. Debt is your nemesis and you're laser-focused on destroying it. Your superpower: when you're debt-free, you'll redirect that intensity into wealth. Your blind spot: you might be too conservative on investing while paying down low-interest debt.",
    tips: [
      { text: 'Use the debt payoff calculator to build your battle plan.', link: '/calculators', cta: 'Open Calculators →' },
      { text: 'Build your personalized debt payoff game plan step by step.', link: '/gameplan', cta: 'Build My Game Plan →' },
      { text: "Learn how to balance debt payoff and investing — it's not either/or.", link: '/playbooks', cta: 'Read the Playbooks →' },
    ],
  },
  C: {
    key: 'C',
    name: 'The Builder',
    emoji: '🏗️',
    color: '#4ade80',
    colorBg: 'rgba(74,222,128,0.15)',
    tagline: "Patient, long-term thinking, steady. You're playing the long game.",
    description:
      "Patient, long-term thinking, steady. You're playing the long game and you know it. Your superpower: you don't panic sell. Your blind spot: sometimes you move too slow on opportunities.",
    tips: [
      { text: 'Model your wealth-building timeline with the investment calculator.', link: '/calculators', cta: 'Open Calculators →' },
      { text: 'Learn about index funds and passive investing strategies.', link: '/playbooks', cta: 'Read the Playbooks →' },
      { text: 'Track your net worth monthly to see your empire growing.', link: '/tracker', cta: 'Open Tracker →' },
    ],
  },
  D: {
    key: 'D',
    name: 'The Avoider',
    emoji: '🦆',
    color: '#fb923c',
    colorBg: 'rgba(251,146,60,0.15)',
    tagline: "You'll start soon. Your superpower: no bad habits to unlearn.",
    description:
      "You know you should probably handle your finances... and you will. Soon. Your superpower: once you get started, you'll make up ground fast — no bad habits to unlearn. Your blind spot: every day you wait costs you compound interest.",
    tips: [
      { text: "Start with the 5-minute compound interest calculator — one number will change everything.", link: '/calculators', cta: 'Open Calculators →' },
      { text: 'Get a simple, no-overwhelm game plan built for you in 2 minutes.', link: '/gameplan', cta: 'Build My Game Plan →' },
      { text: 'Read the Brief daily — 5 minutes of clarity is all it takes to start.', link: '/brief', cta: 'Read the Brief →' },
    ],
  },
};

function scoreAnswers(answers) {
  const counts = { A: 0, B: 0, C: 0, D: 0 };
  answers.forEach((a) => { counts[a]++; });
  const max = Math.max(...Object.values(counts));
  const winners = Object.keys(counts).filter((k) => counts[k] === max);
  if (winners.length === 1) return winners[0];
  // Tie-breaker: last answer wins
  for (let i = answers.length - 1; i >= 0; i--) {
    if (winners.includes(answers[i])) return answers[i];
  }
  return winners[0];
}

export default function MoneyTypeQuiz() {
  const isLoggedIn = (() => { try { return !!localStorage.getItem('knowfi_user'); } catch { return false; } })();

  const [phase, setPhase] = useState('quiz');   // quiz | calculating | teaser | result
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [moneyType, setMoneyType] = useState(null);
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('idle'); // idle | loading | success | error
  const [emailError, setEmailError] = useState('');
  const [fade, setFade] = useState(true);
  const lockedRef = useRef(false);

  const handleAnswer = (label) => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    const newAnswers = [...answers, label];
    const qIndex = currentQ;
    if (qIndex < QUESTIONS.length - 1) {
      setAnswers(newAnswers);
      setCurrentQ(qIndex + 1);
      lockedRef.current = false;
    } else {
      const type = scoreAnswers(newAnswers);
      setAnswers(newAnswers);
      setPhase('calculating');
      setTimeout(() => {
        setMoneyType(type);
        setPhase(isLoggedIn ? 'result' : 'teaser');
        lockedRef.current = false;
      }, 1500);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setEmailStatus('error');
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailStatus('loading');
    try {
      const res = await fetch(`${API}/api/email-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'money-type-quiz' }),
      });
      const data = await res.json();
      if (data.success) {
        setEmailStatus('success');
        setPhase('result');
      } else {
        setEmailStatus('error');
        setEmailError(data.error || 'Something went wrong. Try again.');
      }
    } catch {
      setEmailStatus('error');
      setEmailError('Network error. Please try again.');
    }
  };

  const type = moneyType ? MONEY_TYPES[moneyType] : null;

  return (
    <div style={s.page}>
      <SEO
        title="What's Your Money Type? | KnowFi"
        description="Take the free 2-minute quiz to discover your money personality and get a personalized action plan."
        path="/quiz"
        image="https://knowfi.io/og-quiz.png"
      />
      <div style={s.glow} />

      <div style={s.container}>

        {/* ── QUIZ PHASE ── */}
        {phase === 'quiz' && (
          <div style={s.quizCard}>
            {/* Header */}
            <div style={s.quizHeader}>
              <div style={s.eyebrow}>🧠 Money Type Quiz</div>
              <h1 style={s.quizTitle}>What's Your Money Type?</h1>
              <p style={s.quizSub}>6 questions · 2 minutes · Personalized action plan</p>
            </div>

            {/* Progress */}
            <div style={s.progressWrap}>
              <div style={s.progressTrack}>
                <div style={{ ...s.progressBar, width: `${((currentQ) / QUESTIONS.length) * 100}%` }} />
              </div>
              <span style={s.progressLabel}>{currentQ + 1} / {QUESTIONS.length}</span>
            </div>

            {/* Question — keyed on currentQ so the whole box remounts fresh each question */}
            <div key={currentQ} style={s.questionBox}>
              <div style={s.questionNumber}>Question {currentQ + 1}</div>
              <h2 style={s.questionText}>{QUESTIONS[currentQ].question}</h2>
              <div style={s.optionsGrid}>
                {QUESTIONS[currentQ].options.map((opt) => (
                  <OptionButton key={`q${currentQ}-${opt.label}`} opt={opt} onSelect={() => handleAnswer(opt.label)} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── CALCULATING PHASE ── */}
        {phase === 'calculating' && (
          <div style={s.calcBox}>
            <div style={s.spinner}>⚙️</div>
            <h2 style={s.calcText}>Calculating your Money Type...</h2>
            <p style={s.calcSub}>Analyzing your financial personality</p>
          </div>
        )}

        {/* ── TEASER PHASE ── */}
        {phase === 'teaser' && type && (
          <div style={s.teaserCard}>
            <div style={s.teaserHeader}>
              <div style={s.eyebrow}>Your Result</div>
              <h2 style={s.teaserTitle}>You are...</h2>
            </div>

            {/* Badge */}
            <div style={{ ...s.typeBadge, background: type.colorBg, border: `1px solid ${type.color}` }}>
              <span style={s.typeBadgeEmoji}>{type.emoji}</span>
              <span style={{ ...s.typeBadgeName, color: type.color }}>{type.name}</span>
            </div>

            <p style={s.teaserTagline}>{type.tagline}</p>

            {/* Blurred preview */}
            <div style={s.blurredPreview}>
              <div style={s.blurredContent}>
                <p style={s.blurredText}>{type.description}</p>
                <div style={s.blurredTips}>
                  {type.tips.map((t, i) => (
                    <div key={i} style={s.blurredTip}>✅ {t.text}</div>
                  ))}
                </div>
              </div>
              <div style={s.blurOverlay}>
                <div style={s.lockIcon}>🔒</div>
                <p style={s.lockText}>Your full report is one step away</p>
              </div>
            </div>

            {/* Email gate */}
            <div style={s.gateBox}>
              <h3 style={s.gateTitle}>Enter your email to unlock your full Money Type report</h3>
              <p style={s.gateSub}>Free · No spam · Unsubscribe anytime</p>
              <form onSubmit={handleEmailSubmit} style={s.gateForm}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailStatus('idle'); }}
                  placeholder="your@email.com"
                  style={s.gateInput}
                  disabled={emailStatus === 'loading'}
                />
                <button
                  type="submit"
                  style={{ ...s.gateBtn, ...(emailStatus === 'loading' ? { opacity: 0.7 } : {}) }}
                  disabled={emailStatus === 'loading'}
                >
                  {emailStatus === 'loading' ? 'Unlocking...' : `Unlock My ${type.name} Report →`}
                </button>
              </form>
              {emailStatus === 'error' && (
                <p style={s.gateError}>{emailError}</p>
              )}
            </div>
          </div>
        )}

        {/* ── RESULT PHASE ── */}
        {phase === 'result' && type && (
          <div style={s.resultCard}>
            <div style={s.resultHeader}>
              <div style={{ ...s.typeBadge, background: type.colorBg, border: `1px solid ${type.color}`, marginBottom: '16px' }}>
                <span style={s.typeBadgeEmoji}>{type.emoji}</span>
                <span style={{ ...s.typeBadgeName, color: type.color }}>{type.name}</span>
              </div>
              <h1 style={s.resultTitle}>Your Full Money Type Report</h1>
            </div>

            <div style={s.resultDescription}>
              <p style={s.resultDesc}>{type.description}</p>
            </div>

            <div style={s.tipsSection}>
              <h3 style={s.tipsTitle}>🎯 Your 3 Action Steps</h3>
              {type.tips.map((tip, i) => (
                <div key={i} style={s.tipCard}>
                  <div style={s.tipNumber}>{i + 1}</div>
                  <div style={s.tipContent}>
                    <p style={s.tipText}>{tip.text}</p>
                    <Link to={tip.link} style={{ ...s.tipLink, color: type.color }}>{tip.cta}</Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Share your result */}
            <div style={{ marginTop: '28px', padding: '20px', background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.15)', borderRadius: '12px' }}>
              <p style={{ color: '#c4b5fd', fontSize: '14px', fontWeight: '700', margin: '0 0 12px' }}>
                {type.emoji} Share your money type!
              </p>
              <ShareBar
                text={`I just found out I'm a "${type.name}" money type ${type.emoji}\n\nFind out yours — free quiz on KnowFi 👇`}
                url="https://knowfi.io/quiz"
                variant="quiz"
              />
            </div>

            <div style={s.retakeCta}>
              <button style={s.retakeBtn} onClick={() => { setPhase('quiz'); setCurrentQ(0); setAnswers([]); setMoneyType(null); setEmail(''); setEmailStatus('idle'); setFade(true); }}>
                Retake Quiz
              </button>
              <Link to="/brief" style={s.startBtn}>Start with the KnowFi Brief →</Link>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

function OptionButton({ opt, onSelect }) {
  const touchedRef = useRef(false);
  return (
    <button
      className="quiz-option-btn"
      onTouchEnd={(e) => {
        e.preventDefault(); // prevents the ghost click that follows
        touchedRef.current = true;
        onSelect();
      }}
      onClick={(e) => {
        if (touchedRef.current) return; // already handled by touchEnd
        e.currentTarget.blur();
        onSelect();
      }}
    >
      <span style={s.optionLabel}>{opt.label}</span>
      <span style={s.optionText}>{opt.text}</span>
    </button>
  );
}

const s = {
  page: {
    background: '#0f172a',
    color: '#f8fafc',
    minHeight: '100vh',
    padding: '40px 20px 80px',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: 'inherit',
  },
  glow: {
    position: 'absolute',
    top: '-100px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '700px',
    height: '600px',
    background: 'radial-gradient(ellipse at center, rgba(139,92,246,0.1) 0%, transparent 65%)',
    pointerEvents: 'none',
  },
  container: {
    maxWidth: '640px',
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
  },

  // Quiz card
  quizCard: {
    background: '#1e293b',
    borderRadius: '20px',
    padding: '40px',
    border: '1px solid #334155',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  quizHeader: {
    textAlign: 'center',
    marginBottom: '28px',
  },
  eyebrow: {
    display: 'inline-block',
    background: 'rgba(139,92,246,0.15)',
    border: '1px solid rgba(139,92,246,0.3)',
    color: '#a78bfa',
    padding: '5px 14px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '700',
    marginBottom: '12px',
  },
  quizTitle: {
    fontSize: 'clamp(24px, 4vw, 36px)',
    fontWeight: '900',
    margin: '0 0 8px',
    letterSpacing: '-0.5px',
  },
  quizSub: {
    color: '#64748b',
    fontSize: '14px',
    margin: 0,
  },
  progressWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '28px',
  },
  progressTrack: {
    flex: 1,
    height: '6px',
    background: '#334155',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #a78bfa, #38bdf8)',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  progressLabel: {
    color: '#64748b',
    fontSize: '13px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  questionBox: {
    marginTop: '8px',
  },
  questionNumber: {
    color: '#a78bfa',
    fontSize: '12px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '10px',
  },
  questionText: {
    fontSize: 'clamp(18px, 3vw, 24px)',
    fontWeight: '800',
    margin: '0 0 24px',
    lineHeight: 1.3,
  },
  optionsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  optionBtn: {
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '12px',
    color: '#f1f5f9',
    padding: '14px 18px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    textAlign: 'left',
    transition: 'all 0.15s',
    outline: 'none',
  },
  optionBtnHover: {
    background: 'rgba(139,92,246,0.1)',
    borderColor: 'rgba(139,92,246,0.4)',
    transform: 'translateY(-1px)',
  },
  optionBtnSelected: {
    background: 'rgba(139,92,246,0.2)',
    borderColor: '#7c3aed',
    transform: 'translateY(-1px)',
    outline: 'none',
  },
  optionLabel: {
    background: 'rgba(139,92,246,0.15)',
    color: '#a78bfa',
    borderRadius: '6px',
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: '700',
    flexShrink: 0,
  },
  optionText: {
    lineHeight: 1.4,
  },

  // Calculating
  calcBox: {
    textAlign: 'center',
    padding: '80px 20px',
  },
  spinner: {
    fontSize: '60px',
    marginBottom: '24px',
    display: 'block',
    animation: 'spin 1s linear infinite',
  },
  calcText: {
    fontSize: '24px',
    fontWeight: '800',
    margin: '0 0 10px',
  },
  calcSub: {
    color: '#64748b',
    fontSize: '15px',
    margin: 0,
  },

  // Teaser
  teaserCard: {
    background: '#1e293b',
    borderRadius: '20px',
    padding: '40px',
    border: '1px solid #334155',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  teaserHeader: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  teaserTitle: {
    fontSize: '28px',
    fontWeight: '900',
    margin: '8px 0 0',
  },
  typeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    borderRadius: '50px',
    padding: '10px 20px',
    margin: '0 auto 16px',
    textAlign: 'center',
  },
  typeBadgeEmoji: {
    fontSize: '24px',
  },
  typeBadgeName: {
    fontSize: '22px',
    fontWeight: '900',
    letterSpacing: '-0.3px',
  },
  teaserTagline: {
    color: '#94a3b8',
    fontSize: '16px',
    textAlign: 'center',
    margin: '0 0 24px',
    lineHeight: 1.6,
  },

  // Blurred preview
  blurredPreview: {
    position: 'relative',
    marginBottom: '28px',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  blurredContent: {
    padding: '20px',
    background: '#0f172a',
    borderRadius: '12px',
    filter: 'blur(4px)',
    userSelect: 'none',
  },
  blurredText: {
    color: '#94a3b8',
    fontSize: '15px',
    lineHeight: 1.7,
    margin: '0 0 16px',
  },
  blurredTips: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  blurredTip: {
    color: '#64748b',
    fontSize: '14px',
  },
  blurOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(15,23,42,0.6)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backdropFilter: 'blur(2px)',
  },
  lockIcon: {
    fontSize: '32px',
  },
  lockText: {
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '600',
  },

  // Email gate
  gateBox: {
    background: '#0f172a',
    borderRadius: '14px',
    padding: '28px',
    border: '1px solid rgba(139,92,246,0.2)',
    textAlign: 'center',
  },
  gateTitle: {
    fontSize: '18px',
    fontWeight: '800',
    margin: '0 0 6px',
    lineHeight: 1.3,
  },
  gateSub: {
    color: '#475569',
    fontSize: '13px',
    margin: '0 0 20px',
  },
  gateForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxWidth: '380px',
    margin: '0 auto',
  },
  gateInput: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '10px',
    color: '#f1f5f9',
    padding: '13px 16px',
    fontSize: '15px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  gateBtn: {
    background: '#f59e0b',
    color: '#0f172a',
    border: 'none',
    borderRadius: '10px',
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: '800',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'opacity 0.15s',
  },
  gateError: {
    color: '#f87171',
    fontSize: '13px',
    marginTop: '8px',
  },

  // Result card
  resultCard: {
    background: '#1e293b',
    borderRadius: '20px',
    padding: '40px',
    border: '1px solid #334155',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  resultHeader: {
    textAlign: 'center',
    marginBottom: '28px',
  },
  resultTitle: {
    fontSize: 'clamp(22px, 3.5vw, 30px)',
    fontWeight: '900',
    margin: '0',
    letterSpacing: '-0.5px',
  },
  resultDescription: {
    background: '#0f172a',
    borderRadius: '12px',
    padding: '20px 24px',
    marginBottom: '28px',
    border: '1px solid #1e293b',
  },
  resultDesc: {
    color: '#94a3b8',
    fontSize: '16px',
    lineHeight: 1.7,
    margin: 0,
  },
  tipsSection: {
    marginBottom: '32px',
  },
  tipsTitle: {
    fontSize: '18px',
    fontWeight: '800',
    margin: '0 0 16px',
  },
  tipCard: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    background: '#0f172a',
    borderRadius: '12px',
    padding: '16px 20px',
    marginBottom: '10px',
    border: '1px solid #1e293b',
  },
  tipNumber: {
    background: 'rgba(139,92,246,0.2)',
    color: '#a78bfa',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '700',
    flexShrink: 0,
  },
  tipContent: {
    flex: 1,
  },
  tipText: {
    color: '#cbd5e1',
    fontSize: '14px',
    lineHeight: 1.5,
    margin: '0 0 6px',
  },
  tipLink: {
    fontSize: '13px',
    fontWeight: '700',
    textDecoration: 'none',
  },
  retakeCta: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    paddingTop: '8px',
  },
  retakeBtn: {
    background: 'transparent',
    border: '1px solid #334155',
    color: '#64748b',
    borderRadius: '10px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  startBtn: {
    background: '#38bdf8',
    color: '#0f172a',
    borderRadius: '10px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '800',
    textDecoration: 'none',
    display: 'inline-block',
  },
};
